import React, { useState, useEffect } from "react";
import Modal from "./Modal";
import { getStateStandards } from "../../data/exportedStandards";
import { getState30Hours } from "../../data/exported30Hours";
import { Standard } from "../../types";
import { CourseData } from "../../utils/exportCartridge";
import { toast } from "react-toastify";
import supabase from "../../utils/supabase";

interface ExportCartridgeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (
    selectedStandards: Standard[],
    courseData: CourseData
  ) => Promise<void>;
  isLoading: boolean;
}

interface SelectedStandard {
  id: string;
  title: string;
  selected: boolean;
  chapters: Array<{
    id: string;
    title: string;
    selected: boolean;
  }>;
}

interface State {
  id: number;
  name: string;
}

const ExportCartridgeModal: React.FC<ExportCartridgeModalProps> = ({
  isOpen,
  onClose,
  onExport,
  isLoading,
}) => {
  const [selectedStandards, setSelectedStandards] = useState<
    SelectedStandard[]
  >([]);
  const [states, setStates] = useState<State[]>([]);
  const [selectedState, setSelectedState] = useState<string>("Oklahoma");
  const [loadingStates, setLoadingStates] = useState(false);
  const [courseData, setCourseData] = useState<CourseData>({
    courseId: "PROJECT-EXPLORE-2024",
    courseTitle: "Project Explore",
    courseDescription:
      "K-5 bilingual literacy in English and Spanish, built around animated story episodes at four reading levels.",
    platformType: "90-hours",
  });

  // Fetch states when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchStates();
    }
  }, [isOpen]);

  const fetchStates = async () => {
    setLoadingStates(true);
    try {
      const { data, error } = await supabase
        .from("state")
        .select("id, name")
        .order("name", { ascending: true });

      if (error) {
        console.error("Error fetching states:", error);
        toast.error("Failed to fetch states");
        return;
      }

      setStates(data || []);
    } catch (error) {
      console.error("Error fetching states:", error);
      toast.error("Failed to fetch states");
    } finally {
      setLoadingStates(false);
    }
  };

  // Clear all selections and reset when state changes
  useEffect(() => {
    if (isOpen && selectedState) {
      // Clear all selections
      setSelectedStandards([]);
      // Reset course data to defaults
      setCourseData({
        courseId: `${selectedState}-PROJECT-EXPLORE-2025`,
        courseTitle: "Project Explore",
        courseDescription:
          "K-5 bilingual literacy in English and Spanish, built around animated story episodes at four reading levels.",
        platformType: "90-hours",
      });
    }
  }, [selectedState, isOpen]);

  // Initialize standards when modal opens or state/platform changes
  useEffect(() => {
    if (isOpen && selectedState) {
      // Get state-specific standards
      const standardsToUse =
        courseData.platformType === "30-hours"
          ? getState30Hours(selectedState)
          : getStateStandards(selectedState);

      if (!standardsToUse || standardsToUse.length === 0) {
        setSelectedStandards([]);
        return;
      }

      const initialStandards = standardsToUse.map((standard) => ({
        id: standard.id,
        title: standard.title,
        selected: false,
        chapters: standard.chapters.map((chapter) => ({
          id: chapter.id,
          title: chapter.title,
          selected: false,
        })),
      }));
      setSelectedStandards(initialStandards);
    }
  }, [isOpen, courseData.platformType, selectedState]);

  const handleStandardToggle = (standardId: string) => {
    setSelectedStandards((prev) =>
      prev.map((standard) =>
        standard.id === standardId
          ? {
              ...standard,
              selected: !standard.selected,
              chapters: standard.chapters.map((chapter) => ({
                ...chapter,
                selected: !standard.selected, // Select all chapters when standard is selected, deselect all when unselected
              })),
            }
          : standard
      )
    );
  };

  const handleChapterToggle = (standardId: string, chapterId: string) => {
    setSelectedStandards((prev) =>
      prev.map((standard) =>
        standard.id === standardId
          ? {
              ...standard,
              chapters: standard.chapters.map((chapter) =>
                chapter.id === chapterId
                  ? { ...chapter, selected: !chapter.selected }
                  : chapter
              ),
            }
          : standard
      )
    );
  };

  const handleSelectAll = () => {
    setSelectedStandards((prev) =>
      prev.map((standard) => ({
        ...standard,
        selected: true,
        chapters: standard.chapters.map((chapter) => ({
          ...chapter,
          selected: true,
        })),
      }))
    );
  };

  const handleDeselectAll = () => {
    setSelectedStandards((prev) =>
      prev.map((standard) => ({
        ...standard,
        selected: false,
        chapters: standard.chapters.map((chapter) => ({
          ...chapter,
          selected: false,
        })),
      }))
    );
  };

  const getSelectedCount = () => {
    const selectedStandardsCount = selectedStandards.filter(
      (s) => s.selected
    ).length;
    const selectedChaptersCount = selectedStandards.reduce(
      (total, standard) =>
        total + standard.chapters.filter((c) => c.selected).length,
      0
    );
    return {
      standards: selectedStandardsCount,
      chapters: selectedChaptersCount,
    };
  };

  const handleExport = async () => {
    // Get state-specific standards based on platform type
    const standardsToUse =
      courseData.platformType === "30-hours"
        ? getState30Hours(selectedState)
        : getStateStandards(selectedState);

    if (!standardsToUse || standardsToUse.length === 0) {
      toast.error("No standards available for the selected state.");
      return;
    }

    const selectedStandardsData = selectedStandards
      .filter(
        (standard) =>
          standard.selected ||
          standard.chapters.some((chapter) => chapter.selected)
      )
      .map((standard) => {
        const originalStandard = standardsToUse.find(
          (s) => s.id === standard.id
        );
        if (!originalStandard) return null;

        const selectedChapters = originalStandard.chapters.filter(
          (chapter) =>
            standard.chapters.find((c) => c.id === chapter.id)?.selected
        );

        return {
          ...originalStandard,
          chapters: selectedChapters,
        };
      })
      .filter(
        (standard) => standard && standard.chapters.length > 0
      ) as Standard[];

    if (selectedStandardsData.length === 0) {
      toast.error("Please select at least one standard or chapter to export.");
      return;
    }

    // Validate course data
    if (!courseData.courseId.trim()) {
      toast.error("Please enter a Course ID.");
      return;
    }

    if (!courseData.courseTitle.trim()) {
      toast.error("Please enter a Course Title.");
      return;
    }

    if (!courseData.courseDescription.trim()) {
      toast.error("Please enter a Course Description.");
      return;
    }

    try {
      await onExport(selectedStandardsData, courseData);
    } catch (error) {
      console.error("Export error:", error);
      // Error handling is done in the parent component
    }
  };

  const { standards: selectedStandardsCount, chapters: selectedChaptersCount } =
    getSelectedCount();

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Export Common Cartridge">
      <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
        {/* Course Information */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Course Information
          </h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State
              </label>
              <select
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                disabled={loadingStates}
                className="block w-full rounded-md border-gray-300 shadow-sm px-3 py-2 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                {loadingStates ? (
                  <option>Loading states...</option>
                ) : (
                  states.map((state) => (
                    <option key={state.id} value={state.name}>
                      {state.name}
                    </option>
                  ))
                )}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Select the state to export standards for
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Course ID
              </label>
              <input
                type="text"
                value={courseData.courseId}
                onChange={(e) =>
                  setCourseData((prev) => ({
                    ...prev,
                    courseId: e.target.value,
                  }))
                }
                className="block w-full rounded-md border-gray-300 shadow-sm px-3 py-2 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Course Title
              </label>
              <input
                type="text"
                value={courseData.courseTitle}
                onChange={(e) =>
                  setCourseData((prev) => ({
                    ...prev,
                    courseTitle: e.target.value,
                  }))
                }
                className="block w-full rounded-md border-gray-300 shadow-sm px-3 py-2 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Course Description
              </label>
              <textarea
                value={courseData.courseDescription}
                onChange={(e) =>
                  setCourseData((prev) => ({
                    ...prev,
                    courseDescription: e.target.value,
                  }))
                }
                rows={3}
                className="block w-full rounded-md border-gray-300 shadow-sm px-3 py-2 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Platform Type
              </label>
              <select
                value={courseData.platformType}
                onChange={(e) =>
                  setCourseData((prev) => ({
                    ...prev,
                    platformType: e.target.value as
                      | "90-hours"
                      | "45-hours"
                      | "30-hours",
                  }))
                }
                className="block w-full rounded-md border-gray-300 shadow-sm px-3 py-2 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              >
                <option value="90-hours">90-hours</option>
                <option value="45-hours">45-hours</option>
                <option value="30-hours">30-hours</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {courseData.platformType === "90-hours" &&
                  "Full curriculum with both day one and day two content"}
                {courseData.platformType === "45-hours" &&
                  "Condensed curriculum with day one content only"}
                {courseData.platformType === "30-hours" &&
                  "Streamlined curriculum from 30-hours standards"}
              </p>
            </div>
          </div>
        </div>

        {/* Standards Selection */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-800">
              Select Standards & Chapters
            </h3>
            <div className="flex space-x-2">
              <button
                onClick={handleSelectAll}
                className="text-sm text-indigo-600 hover:text-indigo-500 font-medium"
              >
                Select All
              </button>
              <button
                onClick={handleDeselectAll}
                className="text-sm text-gray-600 hover:text-gray-500 font-medium"
              >
                Deselect All
              </button>
            </div>
          </div>

          <div className="text-sm text-gray-600 mb-4">
            Selected: {selectedStandardsCount} standards,{" "}
            {selectedChaptersCount} chapters
          </div>

          <div className="space-y-4 max-h-96 overflow-y-auto">
            {selectedStandards.map((standard) => (
              <div key={standard.id} className="border rounded-lg p-3">
                <div className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    id={`standard-${standard.id}`}
                    checked={standard.selected}
                    onChange={() => handleStandardToggle(standard.id)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor={`standard-${standard.id}`}
                    className="ml-2 text-sm font-medium text-gray-900 cursor-pointer"
                  >
                    {standard.title}
                    {!standard.selected &&
                      standard.chapters.some((chapter) => chapter.selected) && (
                        <span className="ml-2 text-xs text-indigo-600 bg-indigo-100 px-2 py-1 rounded">
                          Partial
                        </span>
                      )}
                  </label>
                </div>

                {standard.selected && (
                  <div className="ml-6 space-y-2">
                    {standard.chapters.map((chapter) => (
                      <div key={chapter.id} className="flex items-center">
                        <input
                          type="checkbox"
                          id={`chapter-${chapter.id}`}
                          checked={chapter.selected}
                          onChange={() =>
                            handleChapterToggle(standard.id, chapter.id)
                          }
                          className="h-3 w-3 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <label
                          htmlFor={`chapter-${chapter.id}`}
                          className="ml-2 text-sm text-gray-700 cursor-pointer"
                        >
                          {chapter.title}
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Export Information */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-blue-800 mb-2">
            Export Information
          </h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>
              • The exported file will be in Common Cartridge (.imscc) format
            </li>
            <li>
              • Compatible with Canvas, Schoology, Blackboard, and other LMS
              platforms
            </li>
            <li>• Includes all selected content, images, and resources</li>
            <li>• File will be automatically downloaded when ready</li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 sticky bottom-0 bg-white py-2">
          <button
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            className="bg-indigo-500 text-white px-6 py-2 rounded-md hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleExport}
            disabled={
              isLoading ||
              (selectedStandardsCount === 0 && selectedChaptersCount === 0)
            }
          >
            {isLoading ? "Exporting..." : "Export Cartridge"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ExportCartridgeModal;
